// @mui material components
import {
  Snackbar,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
// import DataTable from "theme/Tables/DataTable";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useAuthUser, useAuthHeader } from "react-auth-kit";

import BalanceCard from "app/balances/BalanceCard";
import Transaction from "app/balances/Transaction";
import { useEffect, useState } from "react";
import axios from "axios.js";
import { useIsAuthenticated, useAuthUser } from "react-auth-kit";
import { useNavigate, Link } from "react-router-dom";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";

function Balances() {
  const pageTitle = "Dashboard";
  usePageTitle(pageTitle);

  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated()) {
    const navigate = useNavigate();
    navigate("/sign-in");
  }

  const auth = useAuthUser();
  const user = auth();

  function emptyCache() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
  }

  function versionRefresh() {
    emptyCache();
    window.location.reload();
  }

  function appVersionCheck(newVersion) {
    // Handle App Update
    const currentVersion = localStorage.getItem("_al_version_");
    let shouldUpdate = false;
    if (currentVersion !== newVersion) {
      shouldUpdate = true;
    }
    localStorage.setItem("_al_version_", newVersion);

    if (shouldUpdate) {
      if (
        window.confirm(
          "A new version is available. Alero will automatically refresh to get the latest update."
        ) === true
      ) {
        versionRefresh();
      } else {
        versionRefresh();
      }
    }
  }

  const curColors = {
    EUR: "dark",
    NGN: "success",
    USD: "error",
    GBP: "warning",
  };

  const [balances, setBalances] = useState(null);
  const [transactions, setTransactions] = useState(null);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);
  const [isVerified, setIsVerified] = useState(true);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("users/balances.json")
        .then((result) => {
          setShowLoading(false);
          // console.log(result);
          if (result.data.status === "success") {
            setBalances(result.data.data.balances);
            setTransactions(result.data.data.transactions);
            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("dashboard")) {
                setShowCompanyNotification(true);
              }
            }
            setIsVerified(result.data.data.verified);
          } else {
            setErrMsg(result.data.data.message);
            setOpen(true);
          }

          appVersionCheck(result.data.version);
        })
        .catch(() => {
          setShowLoading(false);
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  // const [exchangeRates, setExchangeRates] = useState([]);

  // const rateColumns = [
  //   { Header: "Pair", accessor: "pair", align: "left" },
  //   { Header: "Buy At", accessor: "buy", align: "center" },
  //   { Header: "Sell At", accessor: "sell", align: "center" },
  // ];

  // const buildRateRows = (data) => {
  //   const rateRows = [];
  //   data.map((item) => {
  //     rateRows.push({
  //       pair: item.pair,
  //       buy: item.buy / 100,
  //       sell: item.sell / 100,
  //     });
  //     return null;
  //   });
  //   return rateRows;
  // };

  // useEffect(() => {
  //   const fetchData = async () => {
  //     await axios
  //       .get("p2p/market/rates.json")
  //       .then((result) => {
  //         // console.log(result);

  //         if (result.data.status === "success") {
  //           const ratesC = buildRateRows(result.data.data.rates);
  //           setExchangeRates(ratesC);
  //         } else {
  //           setErrMsg(result.data.data.message);
  //           setOpen(true);
  //         }
  //       })
  //       .catch(() => {
  //         setErrMsg("An error occurred. Make sure you have an active internet connection.");
  //         setOpen(true);
  //       });
  //   };

  //   fetchData();
  // }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                {!isVerified ? (
                  <MDBox mb={3} mr={1}>
                    <Alert severity="error" sx={{ width: "100%" }}>
                      <AlertTitle>ID Verification Required</AlertTitle>
                      Go to your profile page. <Link to="/profile">My Profile</Link>
                    </Alert>
                  </MDBox>
                ) : (
                  <> </>
                )}
                <MDBox mb={3} sx={{ textAlign: "right" }} mr={1}>
                  <MDTypography
                    variant="caption"
                    fontWeight="light"
                    textTransform="capitalize"
                    pl={2}
                  >
                    ALERO ID: {user.account_identifier} &nbsp;
                    <MDButton variant="outlined" color="dark" iconOnly circular size="small">
                      <Icon
                        onClick={() => {
                          navigator.clipboard.writeText(user.account_identifier);
                          setSeverity("success");
                          setErrMsg("Copied");
                          setOpen(true);
                        }}
                      >
                        copy
                      </Icon>
                    </MDButton>
                  </MDTypography>
                </MDBox>
              </Grid>
              {balances != null ? (
                balances.map((record, index) => (
                  <Grid item xs={12} md={6} lg={3}>
                    <MDBox mb={1.5}>
                      <BalanceCard
                        idx={index}
                        color={curColors[record.currency.currency_code]}
                        currencyCode={record.currency.currency_code}
                        symbol={record.currency.symbol}
                        amount={record.balance}
                        enabled={record.currency.enabled}
                      />
                    </MDBox>
                  </Grid>
                ))
              ) : (
                <>
                  <Grid item xs={12} md={6} lg={3}>
                    <MDBox mb={1.5}>
                      <BalanceCard
                        enabled={1}
                        icon="leaderboard"
                        title="Today's Users"
                        count="2,300"
                        percentage={{
                          color: "success",
                          amount: "+3%",
                          label: "than last month",
                        }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <MDBox mb={1.5}>
                      <BalanceCard
                        color="success"
                        icon="store"
                        title="Revenue"
                        count="34k"
                        percentage={{
                          color: "success",
                          amount: "+1%",
                          label: "than yesterday",
                        }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <MDBox mb={1.5}>
                      <BalanceCard
                        color="primary"
                        icon="person_add"
                        title="Followers"
                        count="+91"
                      />
                    </MDBox>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {/* <Grid item xs={12} md={6} lg={6}>
            <Card sx={{ marginTop: "35px" }}>
              <MDBox pb={2} px={2}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={3}
                  px={2}
                >
                  <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                    Exchange Rates
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: rateColumns, rows: exchangeRates }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
                <MDBox mt={3}>
                  <Alert severity="info" sx={{ width: "100%" }}>
                    Today&apos;s current market rates refreshed every 30 minutes.
                  </Alert>
                </MDBox>
              </MDBox>
            </Card>
          </Grid> */}
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ width: "100%", marginTop: "35px" }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pt={3}
                px={2}
              >
                <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                  Recent Transactions
                </MDTypography>
              </MDBox>
              {transactions === null ? (
                <MDBox pt={3} pb={2} px={2}>
                  <MDBox
                    component="ul"
                    display="flex"
                    flexDirection="column"
                    p={0}
                    m={0}
                    sx={{ listStyle: "none" }}
                  >
                    No recent transactions found
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox pt={3} pb={2} px={2}>
                  <MDBox
                    component="ul"
                    display="flex"
                    flexDirection="column"
                    p={0}
                    m={0}
                    sx={{ listStyle: "none" }}
                  >
                    {transactions.map((record, index) => (
                      <Grid item xs={12} md={12} lg={12}>
                        <MDBox mb={1.5}>
                          <Transaction
                            idx={index}
                            currency={record.currency.symbol}
                            entryType={record.entry_type}
                            narration={record.narration}
                            amount={record.amount}
                            created={record.created}
                          />
                        </MDBox>
                      </Grid>
                    ))}
                  </MDBox>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Balances;
